import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { authClient } from "@/lib/auth-client";
import { vaultDelete, vaultGet, vaultKey, vaultSet } from "./vault-db";

/**
 * A single user-scoped record in the local vault (IndexedDB).
 *
 * Reads are gated on a signed-in session — the record key is namespaced by
 * user id, so each account on a device sees only its own data. Anonymous
 * users get `{ signedIn: false, value: undefined }` and no-op writes, so
 * callers can render unconditionally (same contract as useFavorites).
 */
export function useVaultRecord<T>(namespace: string, key: string | null) {
  const { data: session } = authClient.useSession();
  const userId = session?.user?.id ?? null;
  const signedIn = !!userId;
  const qc = useQueryClient();

  const storageKey =
    userId && key !== null ? vaultKey(userId, namespace, key) : null;
  const queryKey = ["vault", userId, namespace, key] as const;

  const query = useQuery({
    queryKey,
    queryFn: () => vaultGet<T>(storageKey as string).then((v) => v ?? null),
    enabled: !!storageKey,
    // Local reads are cheap but there is no external invalidation source.
    staleTime: Number.POSITIVE_INFINITY,
  });

  const mutation = useMutation({
    mutationFn: async (value: T | null) => {
      if (!storageKey) return value;
      if (value === null) await vaultDelete(storageKey);
      else await vaultSet(storageKey, value);
      return value;
    },
    onSuccess: (value) => qc.setQueryData(queryKey, value),
  });

  const { mutate } = mutation;
  const save = useCallback((value: T) => mutate(value), [mutate]);
  const remove = useCallback(() => mutate(null), [mutate]);

  return {
    signedIn,
    /** undefined while loading; null when no record exists. */
    value: query.data as T | null | undefined,
    /** True once the local read has settled (or the user is anonymous). */
    isLoaded: !signedIn || key === null || query.isSuccess || query.isError,
    save,
    remove,
  };
}
